import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
    FaHome, 
    FaUsers, 
    FaBriefcase, 
    FaClipboardList, 
    FaUserGraduate, 
    FaBullseye, 
    FaNewspaper, 
    FaImages, 
    FaVideo, 
    FaEnvelopeOpenText, 
    FaCog,
    FaFileAlt 
} from 'react-icons/fa';

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [adminData, setAdminData] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Admin menü öğelerini tanımla
    const defaultMenuItems = React.useMemo(() => [
        { path: '/admin/jobs', label: 'İş İlanları', icon: 'FaBriefcase' },
        { path: '/admin/job-applications', label: 'Başvurular', icon: 'FaClipboardList' },
        { path: '/admin/cv-applications', label: 'CV Başvuruları', icon: 'FaUserGraduate' },
        { path: '/admin/talent-applications', label: 'Yetenek', icon: 'FaBullseye' },
        { path: '/admin/blogs', label: 'Blog Yazıları', icon: 'FaNewspaper' },
        { path: '/admin/media', label: 'Medya', icon: 'FaImages' }
    ], []);

    // Kullanıcı girişini kontrol et
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const savedEmail = localStorage.getItem('adminEmail');
                const savedPassword = localStorage.getItem('adminPassword');
                
                if (savedEmail && savedPassword) {
                    const response = await fetch('/api/admin/login', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            email: savedEmail,
                            password: savedPassword
                        })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setUser(data.user);
                        setIsAdmin(true);
                        setAdminData(data.user);
                        setMenuItems(defaultMenuItems);
                    } else {
                        localStorage.removeItem('adminEmail');
                        localStorage.removeItem('adminPassword');
                    }
                }
                
                setIsInitializing(false);
            } catch (error) {
                console.error('Otomatik giriş hatası:', error);
                localStorage.removeItem('adminEmail');
                localStorage.removeItem('adminPassword');
            }
        };

        checkAuth();
    }, [location.pathname]);

    // Yönlendirme fonksiyonu
    const redirectAfterLogin = React.useCallback(() => {
        if (isRedirecting) return;
        
        if (location.pathname === '/admin/login' && user) {
            setIsRedirecting(true);
            navigate('/admin/jobs', { replace: true });
            setTimeout(() => setIsRedirecting(false), 500);
        }
    }, [location.pathname, user, navigate, isRedirecting]);

    // Konum değişikliğini izle
    useEffect(() => {
        if (user && !isRedirecting) {
            redirectAfterLogin();
        }
    }, [location.pathname, user, redirectAfterLogin]);

    // Logout fonksiyonu
    const logout = async () => {
        if (isRedirecting) return;

        setLoading(true);
        try {
            await fetch('/api/admin/logout', { method: 'POST' });
            localStorage.removeItem('adminEmail');
            localStorage.removeItem('adminPassword');
            setUser(null);
            setIsAdmin(false);
            setAdminData(null);
            setMenuItems([]);
            toast.success('Başarıyla çıkış yapıldı.');
            
            setIsRedirecting(true);
            navigate('/admin/login', { replace: true });
            setTimeout(() => setIsRedirecting(false), 500);
        } catch (error) {
            console.error('Çıkış yapılırken hata:', error);
            toast.error('Çıkış yapılırken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Context değeri
    const value = {
        user,
        loading,
        adminData,
        isAdmin,
        isAuthenticated: !!user,
        menuItems,
        logout,
        redirectAfterLogin
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};

export default AdminContext; 